import {
  getExperimentValue,
  isFeatureFlagEnabled,
  type ClientFeatureFlagName,
  type ExperimentName,
  type ExperimentParams
} from '@canvas/client-feature-flags';
import { ReliabilityMetrics, type CanvasMetadata } from '@canvas/frontend-metrics';

import { reliabilityFeatureDimensions } from './reliability-feature-dimensions.ts';

type FeatureSnapshot = {
  experiments: { [Name in ExperimentName]?: Partial<ExperimentParams[Name]> };
  gates: Partial<Record<ClientFeatureFlagName, boolean>>;
};

let cachedReliabilityFeatureMetadata: CanvasMetadata | undefined;
let isReliabilityFeatureMetadataRegistered = false;

function readRegisteredReliabilityFeatureDimensions(): FeatureSnapshot {
  const snapshot: FeatureSnapshot = { experiments: {}, gates: {} };

  for (const dimension of reliabilityFeatureDimensions) {
    switch (dimension.kind) {
      case 'gate':
        snapshot.gates[dimension.featureFlagName] = isFeatureFlagEnabled(
          dimension.featureFlagName
        );
        break;
      case 'experiment': {
        const { experimentName, paramName } = dimension;
        snapshot.experiments[experimentName] = {
          ...snapshot.experiments[experimentName],
          [paramName]: getExperimentValue(experimentName, paramName, {
            fireExperimentExposure: false
          })
        };
        break;
      }
    }
  }

  return snapshot;
}

/**
 * Builds allowlisted metadata from effective application values. Gate booleans
 * describe rollout state; only experiments use the existing cohort mappings.
 */
export function buildReliabilityFeatureMetadata(
  snapshot: FeatureSnapshot = readRegisteredReliabilityFeatureDimensions()
): CanvasMetadata {
  const metadata: CanvasMetadata = {};

  for (const dimension of reliabilityFeatureDimensions) {
    switch (dimension.kind) {
      case 'gate': {
        const value = snapshot.gates[dimension.featureFlagName];
        if (value !== undefined) {
          metadata[`gate_${dimension.featureFlagName}`] = value;
        }
        break;
      }
      case 'experiment': {
        const { experimentName, paramName } = dimension;
        const value = snapshot.experiments[experimentName]?.[paramName];
        if (value !== undefined) {
          metadata[`exp_${experimentName}`] =
            'deriveValue' in dimension && dimension.deriveValue
              ? dimension.deriveValue(value)
              : value;
        }
        break;
      }
    }
  }

  return metadata;
}

/**
 * Exposes the bootstrap snapshot for diagnostics; metric publication uses the
 * registered transformer and never evaluates flags or experiments.
 */
export function getCachedReliabilityFeatureMetadata(): CanvasMetadata | undefined {
  return cachedReliabilityFeatureMetadata;
}

/**
 * Captures effective values once for the page-load lifecycle, after flags and
 * overrides initialize. Runtime override changes require a page reload.
 */
export function initializeReliabilityFeatureMetadata(): CanvasMetadata {
  cachedReliabilityFeatureMetadata = buildReliabilityFeatureMetadata();
  return cachedReliabilityFeatureMetadata;
}

/**
 * Best-effort bootstrap registration: attribution failures must not prevent
 * startup or Reliability publication. Hot paths only merge the cached snapshot.
 */
export function registerReliabilityFeatureMetadata(): void {
  if (isReliabilityFeatureMetadataRegistered) {
    return;
  }

  try {
    initializeReliabilityFeatureMetadata();
    ReliabilityMetrics.getInstance().addCustomMetadataTransformer((metadata) => ({
      ...metadata,
      ...cachedReliabilityFeatureMetadata
    }));
    isReliabilityFeatureMetadataRegistered = true;
  } catch (error) {
    console.error('Unable to register Databases Reliability feature metadata', error);
  }
}

/**
 * Clears page-load state between unit tests.
 */
export function resetReliabilityFeatureMetadataForTests(): void {
  cachedReliabilityFeatureMetadata = undefined;
  isReliabilityFeatureMetadataRegistered = false;
}
